import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { NewsletterForm } from "@/components/Newsletter";
import { SITE, ELYON_VISIBLE } from "@/lib/site";

// TikTok is not in lucide — thin-stroke outline to match the icon set
const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M16.2 3c.4 2.4 2.1 4.2 4.55 4.45v3.15c-1.75 0-3.3-.55-4.55-1.45v6.35a5.8 5.8 0 1 1-5.8-5.8c.25 0 .5.02.75.05v3.25a2.62 2.62 0 1 0 1.85 2.5V3h3.2Z" />
  </svg>
);

const Col = ({ title, children }) => (
  <div>
    <h3 className="mb-5 text-xs font-semibold uppercase tracking-[0.24em] text-white/40">{title}</h3>
    <ul className="space-y-3">{children}</ul>
  </div>
);

const FLink = ({ to, children, testid }) => (
  <li>
    <Link to={to} data-testid={testid} className="text-sm text-white/70 transition-colors duration-300 hover:text-white">
      {children}
    </Link>
  </li>
);

export const Footer = () => {
  const { t } = useTranslation();
  const socials = [
    { Icon: Instagram, href: SITE.social.instagram, label: "Instagram" },
    { Icon: Facebook, href: SITE.social.facebook, label: "Facebook" },
    { Icon: TikTok, href: SITE.social.tiktok, label: "TikTok" },
    { Icon: Youtube, href: SITE.social.youtube, label: "YouTube" },
  ];

  return (
    <footer className="bg-[#111827] text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1.2fr]">
          <div>
            <div className="flex items-center gap-2.5">
              <LogoMark className="h-9 w-9 text-white" />
              <span className="font-display text-[22px] font-bold tracking-tight">
                Nova<span className="font-medium opacity-80">Immo</span>
              </span>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/60">{t("footer.tagline")}</p>
            <div className="mt-6 flex gap-3">
              {socials.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors duration-300 hover:border-[#4DB8FF] hover:text-[#4DB8FF]"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            <div className="mt-8 max-w-xs">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-white/40">{t("newsletter.footerTitle")}</h3>
              <NewsletterForm source="footer" dark compact />
            </div>
          </div>

          <Col title={t("footer.platform")}>
            {ELYON_VISIBLE && <FLink to="/concept" testid="footer-concept-link">{t("nav.concept")}</FLink>}
            <FLink to="/approche" testid="footer-approche-link">{t("nav.approche")}</FLink>
            <FLink to="/projets" testid="footer-projets-link">{t("nav.projets")}</FLink>
            <FLink to="/opportunites" testid="footer-opportunites-link">{t("nav.opportunites")}</FLink>
            <FLink to="/novatv" testid="footer-novatv-link">{t("nav.novatv")}</FLink>
          </Col>

          <Col title={t("footer.company")}>
            <FLink to="/private-network" testid="footer-privatenetwork-link">{t("nav.privatenetwork")}</FLink>
            <FLink to="/conseil-projets-immobiliers" testid="footer-conseil-link">{t("nav.conseil")}</FLink>
            <FLink to="/accompagnement" testid="footer-accompagnement-link">{t("nav.accompagnement")}</FLink>
            <FLink to="/entreprendre" testid="footer-entreprendre-link">{t("nav.entreprendre")}</FLink>
            <FLink to="/investisseurs" testid="footer-investisseurs-link">{t("nav.investisseurs")}</FLink>
            <FLink to="/a-propos" testid="footer-apropos-link">{t("nav.apropos")}</FLink>
            <FLink to="/contact" testid="footer-contact-link">{t("contactPage.kicker")}</FLink>
            <FLink to="/j-ai-un-terrain" testid="footer-terrain-link">{t("nav.cta")}</FLink>
          </Col>

          <Col title={t("footer.legalTitle")}>
            <FLink to="/mentions-legales" testid="footer-mentions-link">{t("footer.legal.mentions")}</FLink>
            <FLink to="/cgu" testid="footer-cgu-link">{t("footer.legal.cgu")}</FLink>
            <FLink to="/politique-de-confidentialite" testid="footer-privacy-link">{t("footer.legal.privacy")}</FLink>
            <FLink to="/politique-cookies" testid="footer-cookies-link">{t("footer.legal.cookies")}</FLink>
            <li>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("novaimmo:open-cookie-banner"))}
                data-testid="footer-manage-cookies-btn"
                className="text-left text-sm text-white/70 transition-colors duration-300 hover:text-white"
              >
                {t("footer.legal.manage")}
              </button>
            </li>
          </Col>

          <div>
            <h3 className="mb-5 text-xs font-semibold uppercase tracking-[0.24em] text-white/40">{t("footer.contact")}</h3>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#4DB8FF]" />
                <span data-testid="footer-address">{SITE.address}<br />{t("footer.country")}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 shrink-0 text-[#4DB8FF]" />
                <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} data-testid="footer-phone" className="transition-colors duration-300 hover:text-white">{SITE.phone}</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0 text-[#4DB8FF]" />
                <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="transition-colors duration-300 hover:text-white">{SITE.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-8 text-xs text-white/40 sm:flex-row sm:items-center">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} {SITE.legalName} — BRN {SITE.brn}. {t("footer.rights")}</p>
          <a
            href="https://novasoft.mu/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 transition-colors duration-300 hover:text-white/70"
            data-testid="footer-novasoft-credit"
          >
            <img src="/images/partner-novasoft-white.png" alt="NovaSoft" className="h-5 w-auto opacity-80" />
            <span>{t("partner.credit")} NovaSoft</span>
          </a>
          <p>{t("footer.madeIn")}</p>
        </div>
      </div>
    </footer>
  );
};
